import { useEffect, useMemo, useState } from "react";

import { CheckIcon } from "@heroicons/react/24/solid";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import {
  DASHBOARD_ROLE_CLAIM,
  emptyFeatureScopes,
  FEATURE_IDS,
  FEATURE_LABELS,
  FeatureId,
  FeatureLevel,
  FeatureScopes,
  levelOptionsFor,
  scopesFromRecord,
  syncVehicleSplitScopes,
  userClaimsToMap,
  visibleFeatureIds,
} from "../../auth/featureScopes";
import {
  LANDING_PAGES,
  landingPageLabel,
  landingPageOptionsFor,
  routeKey,
} from "../../auth/landingPages";
import {
  TOP_NAV_IDS,
  TOP_NAV_LABELS,
  TopNavId,
  TopNavVisibility,
  topNavFromRecord,
} from "../../auth/topNav";
import useCustomerRoles from "../../data/useCustomerRoles";
import useWilmaUsers from "../../data/useWilmaUsers";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../notifications/useNotifications";
import Modal from "../../layouts/Modal";
import { ContactType } from "../../models/Contact";
import { ICustomerRole } from "../../models/customerRole/ICustomerRole";
import { IWilmaUser } from "../../models/auth/WilmaUser";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import PopOutBox from "../shared/PopOutBox";
import classNames from "../../utils/classNames";

interface RoleModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  role: ICustomerRole | undefined;
}

const BASE_TYPES: { value: ContactType; label: string }[] = [
  { value: ContactType.Admin, label: "Admin" },
  { value: ContactType.Staff, label: "Staff" },
  { value: ContactType.Mechanic, label: "Mechanic" },
  { value: ContactType.Driver, label: "Driver" },
  { value: ContactType.Exporter, label: "Exporter" },
];

const userDisplayName = (user: IWilmaUser) => {
  const name = `${user.forename ?? ""} ${user.surname ?? ""}`.trim();
  return name || user.fullname || user.email || "Unknown user";
};

const userMatchesRole = (user: IWilmaUser, roleId: string) => {
  const claims = userClaimsToMap(user.userClaims);
  return claims[DASHBOARD_ROLE_CLAIM]?.toLowerCase() === roleId.toLowerCase();
};

export default function RoleModal({ open, setOpen, role }: RoleModalProps) {
  const { create, update, remove, assignUsers } = useCustomerRoles();
  const { wilmaUsers } = useWilmaUsers();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Role");
  const { addNotification } = useNotification();
  const isTme = CheckFeatureFlag("TME");
  const featureIds = visibleFeatureIds(isTme);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [contactType, setContactType] = useState<ContactType>(ContactType.Staff);
  // Seeded via applyDefaultScopes so pricing starts at full control — onSave
  // persists every feature id, so a 0 here would hide prices on any save.
  const [featureScopes, setFeatureScopes] = useState<FeatureScopes>(() =>
    scopesFromRecord(undefined)
  );
  // "" = automatic (Dashboard, or the first page the role can open).
  const [defaultRoute, setDefaultRoute] = useState("");
  const [topNav, setTopNav] = useState<TopNavVisibility>(() => topNavFromRecord(undefined));
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Admin / Driver / Exporter drive special frontend nav and page behaviour —
  // customers can edit feature levels but not remove or change the base type.
  const isProtectedBuiltIn =
    !!role?.isSystem &&
    (role.contactType === ContactType.Admin ||
      role.contactType === ContactType.Driver ||
      role.contactType === ContactType.Exporter);

  const allUsers = wilmaUsers.data?.data ?? [];

  const members = useMemo(() => {
    if (!role) return [];
    return allUsers
      .filter((user) => userMatchesRole(user, role.id))
      .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)));
  }, [allUsers, role]);

  const memberIdSet = useMemo(
    () => new Set(members.map((user) => user.id)),
    [members]
  );

  const landingOptions = useMemo(
    () => landingPageOptionsFor(featureScopes, isTme),
    [featureScopes, isTme]
  );

  // A stored route this editor doesn't list (set some other way) is kept as-is rather
  // than silently dropped on save.
  const unknownRoute =
    !!defaultRoute &&
    !LANDING_PAGES.some((option) => routeKey(option.path) === routeKey(defaultRoute));

  // Dropping a feature to None removes its pages from the list - don't keep pointing
  // the role at a page it can no longer open.
  useEffect(() => {
    if (!defaultRoute || unknownRoute) return;
    const stillAllowed = landingOptions.some(
      (option) => routeKey(option.path) === routeKey(defaultRoute)
    );
    if (!stillAllowed) setDefaultRoute("");
  }, [landingOptions, defaultRoute, unknownRoute]);

  const candidates = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    return allUsers
      .filter((user) => !memberIdSet.has(user.id))
      .filter((user) => {
        if (!term) return true;
        const haystack = `${user.forename ?? ""} ${user.surname ?? ""} ${
          user.username ?? ""
        } ${user.email ?? ""}`.toLowerCase();
        return haystack.includes(term);
      })
      .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)));
  }, [allUsers, memberIdSet, userSearch]);

  useEffect(() => {
    if (!open) return;

    setUserSearch("");
    setSelectedUserIds([]);

    if (role) {
      setName(role.name ?? "");
      setDescription(role.description ?? "");
      setContactType(role.contactType as ContactType);
      setFeatureScopes(scopesFromRecord(role.featureScopes));
      setDefaultRoute(role.defaultRoute ?? "");
      setTopNav(topNavFromRecord(role.topNavItems));
    } else {
      setName("");
      setDescription("");
      setContactType(ContactType.Staff);
      setDefaultRoute("");
      setTopNav(topNavFromRecord(undefined));
      setFeatureScopes({
        ...emptyFeatureScopes(0),
        dashboard: 2,
        chat: 2,
        leads: 2,
        accounts: 2,
        vehicles: 2,
        opsManagement: 2,
        dismantling: 2,
        parts: 2,
        orderManagement: 2,
        widget: 2,
        pricing: 2,
      });
    }
  }, [role, open]);

  const updateScope = (feature: FeatureId, level: FeatureLevel) => {
    setFeatureScopes((prev) => ({ ...prev, [feature]: level }));
  };

  const toggleTopNav = (id: TopNavId) => {
    setTopNav((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleCandidate = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const onSave = () => {
    if (!name.trim()) return;

    const scopesToSave = syncVehicleSplitScopes(featureScopes, isTme);
    const body = {
      name: name.trim(),
      description: description.trim(),
      contactType,
      featureScopes: FEATURE_IDS.reduce((acc, id) => {
        acc[id] = scopesToSave[id];
        return acc;
      }, {} as Record<string, number>),
      defaultRoute: defaultRoute || null,
      topNavItems: TOP_NAV_IDS.reduce((acc, id) => {
        acc[id] = topNav[id];
        return acc;
      }, {} as Record<string, boolean>),
    };

    if (role) {
      update.mutate(
        { id: role.id, body },
        {
          onSuccess: (res) => {
            saveHandlers.onSuccess();
            if (res?.message) {
              addNotification({
                variant: "success",
                primaryText: res.message,
              });
            }
            setOpen(false);
          },
          onError: saveHandlers.onError,
        }
      );
    } else {
      create.mutate(body, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
        },
        onError: saveHandlers.onError,
      });
    }
  };

  const handleAddUsers = () => {
    if (!role || selectedUserIds.length === 0) return;

    assignUsers.mutate(
      { id: role.id, userIds: selectedUserIds },
      {
        onSuccess: (res) => {
          addNotification({
            variant: "success",
            primaryText: res?.message || "Users added to role",
          });
          setSelectedUserIds([]);
          setUserSearch("");
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Failed to add users to role",
          });
        },
      }
    );
  };

  const handleDelete = () => {
    if (!role) return;
    remove.mutate(role.id, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        setOpen(false);
      },
      onError: deleteHandlers.onError,
    });
  };

  return (
    <>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title="Delete role"
        message="Are you sure you want to delete this role? Staff currently assigned to it must be reassigned first."
        confirmButtonText="Delete"
        onConfirm={handleDelete}
        isLoading={remove.isLoading}
      />
      <Modal width="max-w-5xl" open={open} setOpen={setOpen}>
        <h1 className="text-xl">{`${role ? "Edit" : "Add"} Role`}</h1>
        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Name
                  </label>
                  <input
                    className="w-full mt-1 text-sm text-gray-900 h-[40px]"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Yard Operative"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <label className="text-sm font-medium text-gray-500">
                      Base type
                    </label>
                    <PopOutBox position="left" classes="w-72">
                      <div className="p-2 text-xs text-gray-600 space-y-1.5">
                        <p className="font-medium text-gray-900">
                          What does the base type do?
                        </p>
                        <p>
                          It decides which experience the user logs into and how
                          the rest of Wilma identifies them. Feature access below
                          is separate.
                        </p>
                        <ul className="space-y-1">
                          <li>
                            <span className="font-medium">Staff</span> — the
                            standard dashboard experience.
                          </li>
                          <li>
                            <span className="font-medium">Mechanic</span> — same
                            dashboard as staff, but appears in job assignment
                            lists and dismantling targets.
                          </li>
                          <li>
                            <span className="font-medium">Admin</span> — full
                            dashboard access, ignores feature limits.
                          </li>
                          <li>
                            <span className="font-medium">Driver</span> — the
                            dedicated driver experience (Find Lead).
                          </li>
                          <li>
                            <span className="font-medium">Exporter</span> — the
                            dedicated exports experience.
                          </li>
                        </ul>
                      </div>
                    </PopOutBox>
                  </div>
                  <select
                    aria-label="Base contact type"
                    value={contactType}
                    disabled={isProtectedBuiltIn}
                    onChange={(e) => setContactType(parseInt(e.target.value))}
                    className="w-full mt-1 text-sm text-gray-900 h-[40px] disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    {BASE_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-400">
                    {isProtectedBuiltIn
                      ? "Built-in Admin, Driver and Exporter keep a fixed base type for their dedicated experience."
                      : "Controls login area (staff / driver / exporter / admin)."}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-gray-500">
                    Description
                  </label>
                  <input
                    className="w-full mt-1 text-sm text-gray-900 h-[40px]"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-gray-500">
                    Default page
                  </label>
                  <select
                    aria-label="Default page"
                    value={defaultRoute}
                    onChange={(e) => setDefaultRoute(e.target.value)}
                    className="w-full mt-1 text-sm text-gray-900 h-[40px]"
                  >
                    <option value="">Automatic</option>
                    {unknownRoute && (
                      <option value={defaultRoute}>{defaultRoute}</option>
                    )}
                    {landingOptions.map((option) => (
                      <option key={option.path} value={option.path}>
                        {landingPageLabel(option, isTme)}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-400">
                    Where people on this role land when they open Wilma. Automatic
                    opens the Dashboard, or the first page the role can see if
                    Dashboard is set to None. Only pages the role can open are listed.
                    Applies next sign-in.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-gray-500">
                    Top bar
                  </label>
                  <p className="mt-1 text-xs text-gray-400">
                    Untick anything this role should not see along the top of
                    the screen. Language, the profile menu and Support stay on
                    for everyone. Applies next sign-in.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                    {TOP_NAV_IDS.map((id) => (
                      <label
                        key={id}
                        className="inline-flex items-center gap-2 text-sm text-gray-900"
                      >
                        <input
                          type="checkbox"
                          aria-label={TOP_NAV_LABELS[id]}
                          checked={topNav[id]}
                          onChange={() => toggleTopNav(id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        {TOP_NAV_LABELS[id]}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  Feature access
                </label>
                <p className="mt-1 text-xs text-gray-400">
                  None hides the area. Read and Write are stored for later API
                  enforcement; both allow page access today.
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Vehicle Pricing is enforced by the API. View only makes prices
                  read-only; Hidden also blocks the Payments page. Part prices and
                  invoices are unaffected. Changes apply next sign-in.
                </p>
                <div className="mt-3 overflow-hidden rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Feature
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Access
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {featureIds.map((feature) => (
                        <tr key={feature}>
                          <td className="px-3 py-2 text-gray-900">
                            {FEATURE_LABELS[feature]}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              aria-label={`${FEATURE_LABELS[feature]} access`}
                              value={featureScopes[feature]}
                              onChange={(e) =>
                                updateScope(
                                  feature,
                                  Number(e.target.value) as FeatureLevel
                                )
                              }
                              className="w-full max-w-[10rem] text-sm text-gray-900 h-[36px]"
                            >
                              {levelOptionsFor(feature).map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Users in this role
                </label>
                {!role ? (
                  <p className="mt-2 rounded border border-dashed border-gray-200 bg-gray-50 px-3 py-4 text-sm text-gray-500">
                    Save the role first, then you can add users to it.
                  </p>
                ) : (
                  <>
                    <p className="mt-1 text-xs text-gray-400">
                      Users can be added here. To move someone off this role,
                      reassign them from Staff or another role — removing them
                      here would leave them without a role.
                    </p>
                    <div className="mt-3 max-h-48 overflow-y-auto rounded border border-gray-200">
                      {wilmaUsers.isLoading ? (
                        <p className="px-3 py-4 text-sm text-gray-500">
                          Loading users…
                        </p>
                      ) : members.length === 0 ? (
                        <p className="px-3 py-4 text-sm text-gray-500">
                          No users assigned yet.
                        </p>
                      ) : (
                        members.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-gray-900">
                                {userDisplayName(user)}
                              </p>
                              <p className="truncate text-xs text-gray-500">
                                {user.username || user.email || "—"}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">
                      {members.length} user{members.length === 1 ? "" : "s"}
                    </p>
                  </>
                )}
              </div>

              {role && (
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Add users
                  </label>
                  <p className="mt-1 text-xs text-gray-400">
                    Selecting a user already on another role will move them to
                    this one.
                  </p>
                  <label className="mt-2 flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search staff"
                      className="w-full border-0 bg-transparent p-0 text-sm placeholder:text-gray-400 focus:ring-0"
                    />
                  </label>
                  <div className="mt-2 max-h-56 overflow-y-auto rounded border border-gray-200">
                    {candidates.length === 0 ? (
                      <p className="px-3 py-4 text-sm text-gray-500">
                        {userSearch.trim()
                          ? "No matching users."
                          : "Everyone is already on this role."}
                      </p>
                    ) : (
                      candidates.map((user) => {
                        const checked = selectedUserIds.includes(user.id);
                        return (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => toggleCandidate(user.id)}
                            className={classNames(
                              "flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50",
                              checked ? "bg-primary-50" : ""
                            )}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-gray-900">
                                {userDisplayName(user)}
                              </span>
                              <span className="block truncate text-xs text-gray-500">
                                {user.username || user.email || "—"}
                              </span>
                            </span>
                            <span
                              className={classNames(
                                "flex h-5 w-5 shrink-0 items-center justify-center rounded border",
                                checked
                                  ? "border-primary-600 bg-primary-600 text-white"
                                  : "border-gray-300 bg-white"
                              )}
                            >
                              {checked ? (
                                <CheckIcon className="h-3 w-3" />
                              ) : null}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                  <div className="mt-3">
                    <ButtonInput
                      label={
                        selectedUserIds.length > 0
                          ? `Add ${selectedUserIds.length} user${
                              selectedUserIds.length === 1 ? "" : "s"
                            }`
                          : "Add users"
                      }
                      isSubmit={false}
                      onClick={handleAddUsers}
                      classes={undefined}
                      disabled={selectedUserIds.length === 0}
                      isLoading={assignUsers.isLoading}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ButtonInput
              label="Save"
              isSubmit={false}
              onClick={onSave}
              classes={undefined}
            />
            {role !== undefined && !isProtectedBuiltIn && (
              <ButtonInput
                label="Delete"
                isSubmit={false}
                onClick={() => setDeleteOpen(true)}
                classes="!bg-red-500 hover:!bg-red-600"
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
